import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../firebase/firebaseApp";
import { useAuth } from "../context/AuthContext";

const ROLE_OPTIONS = [
  { value: "clinician", label: "Clinician" },
  { value: "principal_investigator", label: "Principal Investigator" },
  { value: "monitor", label: "Monitor" },
  { value: "chief_investigator", label: "Chief Investigator" },
];

export default function AdminPage() {
  const { claims } = useAuth();
  const db = getFirestore(firebaseApp);
  const functions = getFunctions(firebaseApp, "europe-west2");

  // Cloud Functions
  const createInviteFn = httpsCallable(functions, "createInvite");
  const getUserByEmailFn = httpsCallable(functions, "getUserByEmail");
  const updateUserRoleFn = httpsCallable(functions, "updateUserRole");
  const getClinicUsersFn = httpsCallable(functions, "getClinicUsers");

  // State
  const [activeTab, setActiveTab] = useState("invites"); // 'invites' | 'users'
  const [invites, setInvites] = useState([]);

  // Invite Form State
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "clinician",
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");

  // User Edit State
  const [searchEmail, setSearchEmail] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [userMsg, setUserMsg] = useState("");

  // User List State
  const [clinicUsers, setClinicUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // 1. Listen to Invites (Real-time)
  useEffect(() => {
    if (!claims?.clinic) {
      console.warn(
        "AdminPage: No 'clinic' claim found. Invites will not load."
      );
      return;
    }

    const q = query(
      collection(db, "invites"),
      where("clinic", "==", claims.clinic)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const tA = a.createdAt?.toMillis?.() || 0;
        const tB = b.createdAt?.toMillis?.() || 0;
        return tB - tA;
      });
      setInvites(items);
    });

    return () => unsubscribe();
  }, [claims, db]);

  // 2. Fetch Clinic Users when Tab Changes
  useEffect(() => {
    if (activeTab === "users" && claims?.clinic) {
      const fetchUsers = async () => {
        setListLoading(true);
        try {
          const res = await getClinicUsersFn();
          setClinicUsers(res.data);
        } catch (err) {
          console.error("Failed to load users list", err);
        } finally {
          setListLoading(false);
        }
      };
      fetchUsers();
    }
  }, [activeTab, claims?.clinic]);

  // 3. Invite Logic
  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteMsg("");
    try {
      const res = await createInviteFn({
        email: inviteForm.email,
        role: inviteForm.role,
        expiryDays: 7,
      });
      setInviteMsg(`Success! Code: ${res.data.code}`);
      setInviteForm({ email: "", role: "clinician" });
    } catch (err) {
      console.error(err);
      setInviteMsg(`Error: ${err.message}`);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteInvite = async (id) => {
    if (window.confirm("Are you sure you want to revoke this invite?")) {
      await deleteDoc(doc(db, "invites", id));
    }
  };

  // 4. User Logic
  const handleSearchUser = async (e) => {
    e.preventDefault();
    loadUserForEdit(searchEmail);
  };

  const loadUserForEdit = async (email) => {
    setUserLoading(true);
    setFoundUser(null);
    setSelectedRole("");
    setUserMsg("");
    try {
      const res = await getUserByEmailFn({ email: email });
      setFoundUser(res.data);
      setSelectedRole(res.data.claims?.role || "clinician");
    } catch (err) {
      console.error("Search User Error:", err);
      if (err.message.includes("permission-denied")) {
        setUserMsg("Access Denied: user is in a different clinic.");
      } else if (err.message.includes("not-found")) {
        setUserMsg("User not found in Auth database.");
      } else {
        setUserMsg(`Error: ${err.message}`);
      }
    } finally {
      setUserLoading(false);
    }
  };

  // Clicking a row in the list
  const handleSelectUserFromList = (user) => {
    setSearchEmail(user.email);
    // We can either fetch fresh (safer) or set directly.
    // Fetching fresh ensures we check permissions and get the absolute latest state.
    loadUserForEdit(user.email);
  };

  const handleUpdateRole = async () => {
    if (!foundUser || !selectedRole) return;

    setUserLoading(true);
    try {
      await updateUserRoleFn({
        targetUid: foundUser.uid,
        newRole: selectedRole,
      });
      setFoundUser((prev) => ({
        ...prev,
        claims: { ...prev.claims, role: selectedRole },
      }));
      setUserMsg("Role updated successfully.");

      // Refresh the list to show the new role immediately in the table
      const res = await getClinicUsersFn();
      setClinicUsers(res.data);
    } catch (err) {
      console.error(err);
      setUserMsg(`Update failed: ${err.message}`);
    } finally {
      setUserLoading(false);
    }
  };

  return (
    <div className="w-full h-full p-6 overflow-y-auto mx-auto ">
      <div className="flex flex-col gap-4 mb-6">
        <h1 className="supertitle-text">Admin</h1>
        <p className="muted-text">
          Clinic:{" "}
          {claims?.clinic ? (
            claims.clinic
          ) : (
            <span className="text-red-500 font-bold">No Clinic Assigned</span>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b mb-6">
        <button
          onClick={() => setActiveTab("invites")}
          className={`pb-2 px-4 font-semibold ${
            activeTab === "invites"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          Manage Invites
        </button>
        <button
          onClick={() => setActiveTab("users")}
          className={`pb-2 px-4 font-semibold ${
            activeTab === "users"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          Manage Users
        </button>
      </div>

      {/* --- INVITES TAB --- */}
      {activeTab === "invites" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 bg-gray-50 p-4 rounded shadow-sm h-fit">
            <h3 className="font-semibold mb-4">Create New Invite</h3>
            <form onSubmit={handleCreateInvite} className="flex flex-col gap-3">
              <input
                type="email"
                placeholder="Email Address"
                className="custom-input bg-white"
                required
                value={inviteForm.email}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, email: e.target.value })
                }
              />
              <select
                className="custom-input bg-white"
                value={inviteForm.role}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, role: e.target.value })
                }
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={inviteLoading}
                className="primary-button w-full"
              >
                {inviteLoading ? "Generating..." : "Generate Code"}
              </button>
              {inviteMsg && (
                <p className="text-xs font-semibold text-blue-600 mt-2 break-words">
                  {inviteMsg}
                </p>
              )}
            </form>
          </div>

          <div className="md:col-span-2">
            <h3 className="font-semibold mb-4">Active & Recent Invites</h3>
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        {claims?.clinic
                          ? "No invites found."
                          : "Missing clinic permission."}
                      </td>
                    </tr>
                  )}
                  {invites.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-bold text-primary">
                        {inv.id}
                      </td>
                      <td className="px-4 py-3">{inv.email}</td>
                      <td className="px-4 py-3 capitalize">
                        {inv.role?.replace("_", " ")}
                      </td>
                      <td className="px-4 py-3">
                        {inv.used ? (
                          <span className="text-green-600 bg-green-100 px-2 py-1 rounded text-xs">
                            Used
                          </span>
                        ) : inv.expiresAt?.toMillis() < Date.now() ? (
                          <span className="text-red-600 bg-red-100 px-2 py-1 rounded text-xs">
                            Expired
                          </span>
                        ) : (
                          <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded text-xs">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!inv.used && (
                          <button
                            onClick={() => handleDeleteInvite(inv.id)}
                            className="text-red-600 hover:underline text-xs"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* --- USERS TAB --- */}
      {activeTab === "users" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: List of Users */}
          <div>
            <h3 className="font-semibold mb-4">Users in {claims?.clinic}</h3>
            {listLoading ? (
              <p className="text-sm text-gray-500">Loading users...</p>
            ) : (
              <div className="overflow-x-auto border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0">
                    <tr>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clinicUsers.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="p-4 text-center text-gray-500"
                        >
                          No users found.
                        </td>
                      </tr>
                    )}
                    {clinicUsers.map((u) => (
                      <tr
                        key={u.uid}
                        className={`border-b cursor-pointer hover:bg-blue-50 ${
                          foundUser?.uid === u.uid ? "bg-blue-100" : ""
                        }`}
                        onClick={() => handleSelectUserFromList(u)}
                      >
                        <td className="px-4 py-3 font-medium">{u.email}</td>
                        <td className="px-4 py-3 capitalize">
                          {u.role?.replace("_", " ")}
                        </td>
                        <td className="px-4 py-3">
                          <button className="text-primary font-bold text-xs">
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right: Edit Form */}
          <div>
            <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800">
              <p className="font-bold mb-1">Manage Permissions</p>
              Select a user from the list or search manually to edit their role.
            </div>

            <form onSubmit={handleSearchUser} className="flex gap-2 mb-6">
              <input
                type="email"
                placeholder="Or search email manually..."
                className="custom-input flex-1"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                disabled={userLoading}
                className="primary-button"
              >
                {userLoading ? "Loading..." : "Search"}
              </button>
            </form>

            {userMsg && (
              <p
                className={`mb-4 text-sm font-bold ${
                  userMsg.includes("success") || userMsg.includes("Success")
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {userMsg}
              </p>
            )}

            {foundUser && (
              <div className="border rounded p-6 bg-white shadow-sm animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold">{foundUser.email}</h3>
                    <p className="text-xs text-gray-500">
                      UID: {foundUser.uid.slice(0, 8)}...
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Current Clinic</p>
                    <p className="font-semibold text-primary">
                      {foundUser.claims.clinic || "None"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">Assign Role</label>
                  <div className="flex gap-2 items-center">
                    <select
                      className="custom-input flex-1"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      disabled={userLoading}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleUpdateRole}
                      disabled={userLoading}
                      className="primary-button text-sm whitespace-nowrap"
                    >
                      {userLoading ? "Updating..." : "Confirm Change"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Updates apply immediately. The user may need to refresh
                    their page.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
